import React, { useMemo } from "react";
import { DashboardTypes } from "./types";
import "./i18n";
import "./Dashboard.scss";
import { DashboardCard } from "./components/DashboardCard/DashboardCard";
import CapacityChart from "component/Charts/CapacityChart/CapacityChart";
import { Button } from "@pickit/pickit-components";
import SaturationChart from "component/Charts/SaturationChart/SaturationChart";
import { useTranslation } from "react-i18next";
import NoResultsChart from "./components/noResultsChart/NoResultsChart";
import CapacityDetailModalConatiner from "./components/capacityDetailModal/CapacityDetailModalContainer";
import SimpleFilter from "./components/simpleFilter/SimpleFilter";
import {
  ACTIVE_TRANSACTIONS_STATES,
  ASSIGNED_TRANSACTIONS_STATES,
  COLLECTED_TRANSACTIONS_STATES,
} from "utils/constants";
import DelayedTrips from "./components/delayedTrips/DelayedTrips";
import PointStatus from "./components/pointStatus/PointStatus";

export const DashboardAdmin: React.FC<DashboardTypes> = ({
  dashboard,
  transactionUrlFilter,
  isSingleRegion,
  filters,
  previousFilters,
  countryCode,
  regions,
  showModal,
  testId,
  getPointStatus,
  getDelayedTrips,
  setTripPage,
  toggleModal,
  refresh,
}): JSX.Element => {
  const { t } = useTranslation("dashboard");
  const regionsSelected = useMemo(
    () => filters.regions?.split(","),
    [filters.regions]
  );

  return (
    <div className="card-admin">
      <div className="dashboard-top-container">
        <div className="dashboard-title-container">
          <h3 data-testId={`${testId}-title`}>{t("title")}</h3>
          <div className="dashboard-filter-container">
            <h2
              className="dashboard-selected-regions"
              data-testId={`${testId}-regions-selected`}
            >
              {regions.length > 0 &&
                regionsSelected?.map(
                  (region, i) =>
                    `${regions?.find((r) => r.id === region)?.name}${
                      i < regionsSelected.length - 1 ? ", " : ""
                    }`
                )}
            </h2>
            <SimpleFilter
              regions={regions}
              testId={`${testId}-filter`}
              value={filters.regions}
            />
          </div>
        </div>
        <div className="dashboard-refresh">
          <Button
            label={t("refresh")}
            hierarchy="secondary"
            fullWidth
            size="small"
            onClick={refresh}
            testId={`${testId}-refresh-button`}
          />
        </div>
      </div>
      <div className="card-admin-row cards-container">
        <div className="card-admin-col-3">
          <DashboardCard
            title={t("activeTransactions")}
            count={dashboard.transactionsDetail.active}
            url={`transaction?state=${ACTIVE_TRANSACTIONS_STATES.join(
              ","
            )}&${transactionUrlFilter}`}
            testId={`${testId}-active-transactions`}
          />
        </div>
        <div className="card-admin-col-3">
          <DashboardCard
            title={t("assignedTransactions")}
            count={dashboard.transactionsDetail.assigned}
            url={`transaction?state=${ASSIGNED_TRANSACTIONS_STATES.join(
              ","
            )}&${transactionUrlFilter}`}
            type="precentage"
            total={dashboard.transactionsDetail.active}
            testId={`${testId}-assigned-transactions`}
          />
        </div>
        <div className="card-admin-col-3">
          <DashboardCard
            title={t("collectedTransactions")}
            count={dashboard.transactionsDetail.collected}
            url={`transaction?state=${COLLECTED_TRANSACTIONS_STATES.join(
              ","
            )}&${transactionUrlFilter}`}
            type="precentage"
            total={dashboard.transactionsDetail.assigned}
            testId={`${testId}-collected-transactions`}
          />
        </div>
        <div className="card-admin-col-3">
          <DashboardCard
            title={t("delayedTransactions")}
            count={dashboard.transactionsDetail.inDelay}
            type="precentage"
            total={dashboard.transactionsDetail.assigned}
            testId={`${testId}-delayed-transactions`}
          />
        </div>
      </div>
      <div className="card-admin-row">
        <div className=" card-admin-col-7">
          <div className="chart-container">
            {dashboard.slotCapacity.length > 0 &&
            dashboard.slotCapacity.some((slot) => slot.capacity > 0) ? (
              <div className="chart-padding">
                <CapacityChart
                  data={dashboard.slotCapacity}
                  previousFilters={previousFilters}
                  barHeight={13}
                  regions={regions}
                  shouldOpenDetail={isSingleRegion}
                />
              </div>
            ) : (
              <NoResultsChart chart="capacity" />
            )}
          </div>
        </div>
        <div className="card-admin-col-5">
          <div className="chart-container">
            {dashboard.saturation.length > 0 &&
            dashboard.saturation.some((saturation) => saturation.total > 0) ? (
              <div className="chart-padding">
                <SaturationChart
                  data={dashboard.saturation}
                  color="#0098ff"
                  barHeight={13}
                  isSingleRegion={isSingleRegion}
                  regions={filters.regions}
                />
              </div>
            ) : (
              <NoResultsChart chart="saturation" />
            )}
          </div>
        </div>
      </div>
      <div className="card-admin-row tables-container">
        <div className="card-admin-col-7">
          <DelayedTrips
            delayedTrips={dashboard.delayedTrips}
            countryCode={countryCode}
            quantity={dashboard.tripsQuantity}
            currentPage={dashboard.tripsCurrentPage}
            getDelayedTrips={getDelayedTrips}
            filters={filters}
            setTripPage={setTripPage}
          />
        </div>
        <div className="card-admin-col-5">
          <PointStatus
            pointStatus={dashboard.points}
            countryCode={countryCode}
            currentPage={dashboard.pointsCurrentPage}
            quantity={dashboard.pointsQuantity}
            getPointStatus={getPointStatus}
            filters={filters}
          />
        </div>
      </div>
      <CapacityDetailModalConatiner
        isOpen={showModal}
        toggleModal={toggleModal}
      />
    </div>
  );
};
